import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileDropzone } from '../components/upload/FileDropzone';
import { uploadDocument } from '../services/api';
import { BRAND_CODE, BRAND_NAME } from '../config/branding';

// Core consultant reports — always required for a DD assessment.
const DD_TYPES = [
  { value: 'InvestmentMemo', label: 'Investment Memo',     desc: 'IRR, risk narrative, deal thesis' },
  { value: 'FinancialDD',    label: 'Financial DD Report', desc: 'Audited financials, projections' },
  { value: 'TechnicalDD',    label: 'Technical DD Report', desc: 'Engineering specs, IP review' },
];

// Optional standard docs the analyst toggles on per deal — currently
// just IC Resolution, which provides board-approved PE classification.
const DD_OPTIONAL_TYPES = [
  { value: 'ICResolution', label: 'IC Resolution', desc: 'Signed IC approval / PE classification source' },
];

const PROCESSING_STEPS = [
  'Document Upload',
  'Text Extraction (OCR)',
  'Embedding Generation',
  'AI Analysis',
  'Benchmark Classification',
  'Report Generation',
];

type FileMap = Record<string, File | null>;
type SelectedMap = Record<string, boolean>;

interface OptionalDoc {
  id: string;
  label: string;
  file: File | null;
}

export function DDUploadPage() {
  const navigate = useNavigate();
  /* Sprint 5 — when this page is rendered under the
     /projects/:auditId/dd/upload route, useParams supplies the
     project context. The banner below makes the project linkage
     visible to the analyst. */
  const { auditId } = useParams<{ auditId?: string }>();
  const [files, setFiles] = useState<FileMap>({
    InvestmentMemo: null, FinancialDD: null, TechnicalDD: null,
    ICResolution: null,
  });
  // The three core docs (Investment Memo, Financial DD, Technical DD)
  // are always mandatory. ICResolution is opt-in — toggle on for PE
  // deals where the IC Resolution drives the PE classification source.
  const [selected, setSelected] = useState<SelectedMap>({
    InvestmentMemo: true, FinancialDD: true, TechnicalDD: true,
    ICResolution: false,
  });
  const toggleOptionalType = (type: string) =>
    setSelected((prev) => {
      const next = { ...prev, [type]: !prev[type] };
      if (!next[type]) setFiles((f) => ({ ...f, [type]: null }));
      return next;
    });
  const [optionalDocs, setOptionalDocs] = useState<OptionalDoc[]>([]);
  const [dealName, setDealName] = useState('');
  const [analystName, setAnalystName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setFile = (type: string) => (file: File | null) =>
    setFiles((prev) => ({ ...prev, [type]: file }));

  const addOptionalDoc = () => {
    // crypto.randomUUID() is only available in secure (HTTPS) contexts —
    // the server is served over HTTP, so calling it throws TypeError and
    // bubbles the click into the surrounding form's submit handler.
    // Use a non-cryptographic ID; uniqueness inside this component list
    // is all we need.
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    // Newest first — the add control sits at the top of the section.
    setOptionalDocs((prev) => [{ id, label: '', file: null }, ...prev]);
  };
  const updateOptionalDoc = (id: string, patch: Partial<OptionalDoc>) => {
    setOptionalDocs((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  };
  const removeOptionalDoc = (id: string) => {
    setOptionalDocs((prev) => prev.filter((d) => d.id !== id));
  };

  // Core 3 are always required; optional standard types only when
  // toggled on. The submit path uploads all "active" entries.
  const activeOptionalTypes = DD_OPTIONAL_TYPES.filter((t) => selected[t.value]);
  const activeTypes = [...DD_TYPES, ...activeOptionalTypes];
  const hasAnyType = activeTypes.length > 0;
  const allSelectedHaveFiles = hasAnyType && activeTypes.every((t) => files[t.value]);
  const missingTypes = activeTypes.filter((t) => !files[t.value]).map((t) => t.label);

  /* Sprint 5 client feedback 2026-06-20: per-file upload progress.
     The prior submit handler did `await uploadDocument(...)` in a loop
     and the button just said "Uploading N files…" until everything
     finished, then jumped straight to /processing/<id>. Analyst feedback
     was "the page suddenly directs me away without showing progress" —
     they didn't see individual files tick off. Now we track each
     file's status in state and surface a real per-file checklist below
     the submit button. Navigation still happens after all files
     complete, but only after a brief pause so the analyst sees the
     final "all uploaded" state before the route changes. */
  type UploadItemStatus = 'pending' | 'uploading' | 'done' | 'failed';
  type UploadItem = {
    key: string;
    label: string;
    filename: string;
    status: UploadItemStatus;
    error?: string;
  };
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasAnyType) { setError('Select at least one document type.'); return; }
    if (!allSelectedHaveFiles) {
      setError(`Upload a file for each selected document type. Missing: ${missingTypes.join(', ')}.`);
      return;
    }
    setError(null);
    setIsSubmitting(true);

    const entries = activeTypes.filter((t) => files[t.value]);
    const validOptional = optionalDocs.filter((d) => d.file && d.label.trim().length > 0);

    // Seed the per-file progress list so the analyst sees the queue
    // immediately when they click Upload.
    const initialItems: UploadItem[] = [
      ...entries.map((t) => ({
        key: `m:${t.value}`,
        label: t.label,
        filename: files[t.value]!.name,
        status: 'pending' as UploadItemStatus,
      })),
      ...validOptional.map((od, i) => ({
        key: `o:${i}`,
        label: od.label || 'Optional document',
        filename: od.file!.name,
        status: 'pending' as UploadItemStatus,
      })),
    ];
    setUploadItems(initialItems);

    const setItem = (key: string, patch: Partial<UploadItem>) => {
      setUploadItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
    };

    try {
      let assessmentId: string | null = null;
      for (const t of entries) {
        const key = `m:${t.value}`;
        setItem(key, { status: 'uploading' });
        try {
          const response = await uploadDocument(files[t.value]!, {
            module_type: 'DD',
            document_subtype: t.value,
            deal_name: dealName || undefined,
            analyst_name: analystName || undefined,
            assessment_id: assessmentId || undefined,
            /* Sprint 5 — project-scoped upload links the new
               Assessment row to the Project's Stage 1. Without this,
               uploads create standalone DDs disconnected from the
               Project Workspace. */
            project_audit_id: auditId || undefined,
          });
          if (!assessmentId) assessmentId = response.assessment_id;
          setItem(key, { status: 'done' });
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { detail?: { message?: string } } } })?.response?.data
              ?.detail?.message ?? 'Upload failed';
          setItem(key, { status: 'failed', error: typeof msg === 'string' ? msg : 'Upload failed' });
          throw err;  // bubble out so the catch below sets the page-level error too
        }
      }
      for (const [i, od] of validOptional.entries()) {
        const key = `o:${i}`;
        setItem(key, { status: 'uploading' });
        try {
          await uploadDocument(od.file!, {
            module_type: 'DD',
            document_subtype: 'Other',
            deal_name: dealName || undefined,
            analyst_name: analystName || undefined,
            assessment_id: assessmentId || undefined,
            project_audit_id: auditId || undefined,
          });
          setItem(key, { status: 'done' });
        } catch (err: unknown) {
          const msg =
            (err as { response?: { data?: { detail?: { message?: string } } } })?.response?.data
              ?.detail?.message ?? 'Upload failed';
          setItem(key, { status: 'failed', error: typeof msg === 'string' ? msg : 'Upload failed' });
          throw err;
        }
      }
      // 1.2 sec pause so the analyst sees the final "all done" state
      // before the route changes — without this the green checks flash
      // for a single frame and feel like nothing visible happened.
      await new Promise((r) => setTimeout(r, 1200));
      if (assessmentId) {
        /* Sprint 5 fix — `/processing/{id}` reads per-DOCUMENT status
           (the route uses useJobStatus → /documents/:jobId/status) and
           flips to "completed" the moment one doc finishes ingestion —
           before AI analysis even starts. The Project Workspace then
           correctly shows the stage as `in_progress` and the two
           surfaces disagree. `/assessments/{id}` reads ASSESSMENT-level
           status (useAssessmentStatus → /assessments/:id/status) which
           only flips to completed after the full pipeline (ingest →
           analyse → report) finishes. Route there instead. */
        const ret = auditId ? `?return_to=/projects/${auditId}` : '';
        navigate(`/assessments/${assessmentId}${ret}`);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: { message?: string } } } })?.response?.data
          ?.detail?.message ?? 'Upload failed. Please try again.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const mandatoryUploaded = Object.entries(files).filter(([k, v]) => v && selected[k]).length;
  const optionalUploaded = optionalDocs.filter((d) => d.file && d.label.trim().length > 0).length;
  const uploadCount = mandatoryUploaded + optionalUploaded;

  return (
    <div className="module-dd">
      <div className="module-page-header">
        {auditId && (
          <div
            style={{
              padding: 'var(--s3) var(--s4)',
              background: 'var(--dd-accent-bg)',
              border: '1px solid var(--dd-accent-border)',
              borderRadius: 'var(--r-md)',
              marginBottom: 'var(--s4)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: '0.9rem' }}>
              Uploading Stage 1 (DD) documents for project{' '}
              <strong style={{ fontFamily: 'var(--font-mono)' }}>
                {auditId}
              </strong>
            </span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate(`/projects/${auditId}`)}
            >
              ← Back to project
            </button>
          </div>
        )}
        <div className="breadcrumb">
          {auditId ? (
            <>
              <a href="/projects" onClick={(e) => { e.preventDefault(); navigate('/projects'); }}>Projects</a>
              <span className="bc-sep">/</span>
              <a href={`/projects/${auditId}`} onClick={(e) => { e.preventDefault(); navigate(`/projects/${auditId}`); }}>{auditId}</a>
              <span className="bc-sep">/</span>
              <span>Stage 1 — DD Upload</span>
            </>
          ) : (
            <>
              <a href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>Dashboard</a>
              <span className="bc-sep">/</span>
              <span>DD Assessments</span>
              <span className="bc-sep">/</span>
              <span>New Assessment</span>
            </>
          )}
        </div>
        <div className="report-title-row">
          <div>
            <p className="module-eyebrow">DD Module</p>
            <h1 className="t-h1">New Due Diligence Assessment</h1>
          </div>
          <div className="workflow-indicator">
            <div className="wi-step active"><div className="wi-num">1</div><span>Upload</span></div>
            <div className="wi-line" />
            <div className="wi-step"><div className="wi-num">2</div><span>Processing</span></div>
            <div className="wi-line" />
            <div className="wi-step"><div className="wi-num">3</div><span>Report</span></div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <form onSubmit={handleSubmit}>
          <div className="upload-layout">
            <div className="upload-main">

              {/* Step 1 — Document Type (mandatory, all 3 required — UI-26) */}
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="t-h3">Required Documents <span style={{ color: 'var(--fail)', marginLeft: 2 }}>*</span></h2>
                    <p className="t-xs t-muted t-mt-2px">All three are required for a DD assessment</p>
                  </div>
                  <span className="badge badge-neutral">Step 1 of 3</span>
                </div>

                <div className="doctype-tiles">
                  {DD_TYPES.map((t) => (
                    <div
                      key={t.value}
                      className="doctype-tile selected"
                      aria-pressed="true"
                      style={{ cursor: 'default' }}
                    >
                      <div className="doctype-tile-body">
                        <div className="doctype-tile-title">{t.label}</div>
                        <div className="doctype-tile-desc">{t.desc}</div>
                      </div>
                      <span className="doctype-tile-check on" aria-hidden="true">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </span>
                    </div>
                  ))}
                  {DD_OPTIONAL_TYPES.map((t) => {
                    const isOn = selected[t.value];
                    return (
                      <button
                        key={t.value}
                        type="button"
                        className={`doctype-tile${isOn ? ' selected' : ''}`}
                        onClick={() => toggleOptionalType(t.value)}
                        aria-pressed={isOn}
                      >
                        <div className="doctype-tile-body">
                          <div className="doctype-tile-title">
                            {t.label}
                            <span className="t-xs t-muted" style={{ marginLeft: 6 }}>(Optional)</span>
                          </div>
                          <div className="doctype-tile-desc">{t.desc}</div>
                        </div>
                        <span className={`doctype-tile-check${isOn ? ' on' : ''}`} aria-hidden="true">
                          {isOn && (
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Step 2 — Upload Documents */}
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="t-h3">Upload Documents</h2>
                    <p className="t-xs t-muted t-mt-2px">PDF, DOCX, XLSX, PPTX — up to 50 MB</p>
                  </div>
                  <span className="badge badge-neutral">Step 2 of 3</span>
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); addOptionalDoc(); }}
                  style={{ marginBottom: 'var(--s3)' }}
                >
                  + Add optional document
                </button>

                {/* Optional documents (UI-26): analyst-supplied extras with custom type
                    names. Newest first, above the standard document list. */}
                {optionalDocs.map((doc) => (
                  <div key={doc.id} style={{ marginBottom: 'var(--s3)', paddingBottom: 'var(--s3)', borderBottom: '1px dashed var(--lines)' }}>
                    <div style={{ display: 'flex', gap: 'var(--s2)', alignItems: 'center', marginBottom: 6 }}>
                      <input
                        className="form-input"
                        type="text"
                        placeholder="Optional document type (e.g. ESG Report, Legal Opinion)"
                        value={doc.label}
                        onChange={(e) => updateOptionalDoc(doc.id, { label: e.target.value })}
                        style={{ flex: 1 }}
                      />
                      {doc.file ? (
                        <span className="badge badge-pass">File ready</span>
                      ) : (
                        <span className="badge badge-neutral">No file</span>
                      )}
                      <button
                        type="button"
                        className="btn btn-accent btn-sm"
                        onClick={() => removeOptionalDoc(doc.id)}
                        aria-label="Remove optional document"
                      >
                        Remove
                      </button>
                    </div>
                    <FileDropzone
                      onFile={(f) => updateOptionalDoc(doc.id, { file: f })}
                      onClear={() => updateOptionalDoc(doc.id, { file: null })}
                    />
                  </div>
                ))}

                {activeTypes.map((t) => (
                  <div key={t.value} style={{ marginBottom: 'var(--s3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span className="t-label">{t.label}</span>
                      {files[t.value] ? (
                        <span className="badge badge-pass">File ready</span>
                      ) : (
                        <span className="badge badge-neutral">No file</span>
                      )}
                    </div>
                    <FileDropzone onFile={setFile(t.value)} onClear={setFile(t.value)} />
                  </div>
                ))}

                <div className="alert alert-pass" style={{ marginTop: 'var(--s3)' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  <span><strong>Air-gapped processing</strong> — no data leaves the {BRAND_NAME} network.</span>
                </div>
              </div>

              {/* Step 3 — Deal Context */}
              <div className="card">
                <div className="card-header">
                  <div>
                    <h2 className="t-h3">Deal Context <span className="t-muted t-optional">(Optional)</span></h2>
                  </div>
                  <span className="badge badge-neutral">Step 3 of 3</span>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="dealName">Deal name</label>
                    <input
                      className="form-input"
                      type="text"
                      id="dealName"
                      placeholder="e.g. Project Helios"
                      value={dealName}
                      onChange={(e) => setDealName(e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="analyst">Lead analyst</label>
                    <input
                      className="form-input"
                      type="text"
                      id="analyst"
                      placeholder="e.g. Arjun Singh"
                      value={analystName}
                      onChange={(e) => setAnalystName(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="alert alert-fail" role="alert">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {error}
                </div>
              )}

              <div className="upload-submit-row">
                <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)} disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={isSubmitting || !allSelectedHaveFiles}
                >
                  {isSubmitting ? (
                    <><span className="spinner" /> Uploading {uploadCount} file{uploadCount !== 1 ? 's' : ''}…</>
                  ) : (
                    <>
                      Start Processing{uploadCount > 0 ? ` (${uploadCount} file${uploadCount !== 1 ? 's' : ''})` : ''}
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </>
                  )}
                </button>
              </div>

              {/* Per-file progress panel — only visible while a submit
                  is in flight (or just finished). Each file shows its
                  status with a tiny spinner / check / error icon so the
                  analyst can see the upload happen rather than feeling
                  "the page jumped to processing without warning". */}
              {uploadItems.length > 0 && (
                <div
                  className="card"
                  style={{
                    marginTop: 'var(--s4)',
                    padding: 'var(--s4)',
                  }}
                  aria-live="polite"
                >
                  <div
                    style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      marginBottom: 'var(--s3)',
                      color: 'var(--text)',
                    }}
                  >
                    Upload progress
                    {' — '}
                    <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                      {uploadItems.filter((i) => i.status === 'done').length}{' '}
                      of {uploadItems.length} uploaded
                    </span>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
                    {uploadItems.map((it) => (
                      <li
                        key={it.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '6px 10px',
                          background:
                            it.status === 'done'
                              ? 'color-mix(in srgb, var(--pass) 8%, transparent)'
                              : it.status === 'failed'
                              ? 'color-mix(in srgb, var(--fail) 10%, transparent)'
                              : 'var(--surface-alt)',
                          borderRadius: 'var(--r-sm)',
                          fontSize: '0.85rem',
                        }}
                      >
                        <span style={{ display: 'inline-flex', width: 18, justifyContent: 'center' }}>
                          {it.status === 'uploading' && <span className="spinner spinner-sm" />}
                          {it.status === 'done' && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--pass)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                          {it.status === 'failed' && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fail)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          )}
                          {it.status === 'pending' && (
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--lines)' }} />
                          )}
                        </span>
                        <span style={{ fontWeight: 500 }}>{it.label}</span>
                        <span style={{ color: 'var(--muted)', fontSize: '0.78rem', flex: 1 }}>
                          {it.filename}
                        </span>
                        <span
                          style={{
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color:
                              it.status === 'done' ? 'var(--pass)'
                              : it.status === 'failed' ? 'var(--fail)'
                              : it.status === 'uploading' ? 'var(--info)'
                              : 'var(--muted)',
                          }}
                        >
                          {it.status === 'uploading' ? 'Uploading…' : it.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {uploadItems.every((i) => i.status === 'done') && (
                    <div
                      style={{
                        marginTop: 'var(--s3)',
                        fontSize: '0.82rem',
                        color: 'var(--pass)',
                        fontWeight: 600,
                      }}
                    >
                      ✓ All files uploaded — opening live processing page…
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="upload-sidebar">
              <div className="card card-sm">
                <p className="t-label t-label-mb3">Processing steps</p>
                <div className="upload-steps-list">
                  {PROCESSING_STEPS.map((step, i) => (
                    <div key={i} className={`upload-step-row ${i === 0 ? 'active' : 'inactive'}`}>
                      <div className={`upload-step-circle-sm ${i === 0 ? 'active' : 'inactive'}`}>{i + 1}</div>
                      {step}
                    </div>
                  ))}
                </div>
              </div>
              <div className="card card-sm">
                <p className="t-label t-label-mb3">Benchmark</p>
                <div className="upload-sidebar-bench">
                  <div className="upload-bench-id">{BRAND_CODE}-INF-RE-2024</div>
                  <p className="t-xs t-muted" style={{ margin: 0 }}>Infrastructure RE · MENA · 2024</p>
                </div>
              </div>
              <div className="card card-sm">
                <p className="t-label t-label-mb3">Supported formats</p>
                <div className="upload-format-list">
                  {['PDF','DOCX','XLSX','PPTX'].map((fmt) => (
                    <div key={fmt} className="upload-format-row">
                      <span className="upload-format-key">{fmt}</span>
                      <span className="t-muted t-xs">Up to 50 MB</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
